import React, { useRef, useEffect, useState } from 'react';
import { useChatContext } from '../../context/ChatContext';
import useUnifiedChats from '../../hooks/useUnifiedChats';
import ChatController from './ChatController';
import './ChatWidget.css';

const ChatWidget = () => {
    const { isChatOpen, setIsChatOpen, closeChat, activeChat } = useChatContext();

    // 👂 Instance #1 of the socket listeners starts here to track unreadMap
    const { unreadMap } = useUnifiedChats();

    const widgetRef = useRef(null);
    const [activeTab, setActiveTab] = useState('all');
    const [isMobile, setIsMobile] = useState(
        typeof window !== 'undefined' ? window.innerWidth < 768 : false
    );

    // Calculate total unread messages
    const totalUnread = Object.values(unreadMap || {}).reduce((a, b) => a + Number(b), 0);

    // 🔍 DIAGNOSTIC LOG
    useEffect(() => {
        if (isChatOpen) {
            console.log("🛠️ [ChatWidget State]:", {
                activeChatId: activeChat?._id || 'NONE',
                totalUnread
            });
        }
    }, [isChatOpen, activeChat, totalUnread]);

    // Handle Window Resize
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Close on click outside (Desktop)
    useEffect(() => {
        if (!isChatOpen || isMobile) return;
        const handleClickOutside = (e) => {
            if (widgetRef.current && !widgetRef.current.contains(e.target)) {
                closeChat();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isChatOpen, closeChat, isMobile]);

    // Auto-open when a chat is selected
    useEffect(() => {
        if (activeChat && !isChatOpen) {
            setIsChatOpen(true);
        }
    }, [activeChat, isChatOpen, setIsChatOpen]);

    return (
        <>
            {/* Floating toggle button */}
            {!isChatOpen && (
                <button
                    className="chat-toggle"
                    aria-label="Open chat"
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsChatOpen(true);
                    }}
                >
                    <span className="chat-icon">💬</span>
                    {totalUnread > 0 && (
                        <span className="widget-total-unread-badge">
                            {totalUnread > 9 ? '9+' : totalUnread}
                        </span>
                    )}
                </button>
            )}

            {/* Main Widget Container */}
            <div
                className={`chat-widget ${isChatOpen ? 'is-open' : 'is-closed'}`}
                ref={widgetRef}
                style={{
                    position: 'fixed',
                    bottom: '80px',
                    right: '20px',
                    zIndex: 99999,
                    display: isChatOpen ? 'block' : 'none',
                }}
            >
                <ChatController
                    isOpen={isChatOpen}
                    setIsOpen={setIsChatOpen}
                    isMobile={isMobile}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                />
            </div>
        </>
    );
};

export default ChatWidget;